import * as Popover from '@radix-ui/react-popover';
import { CalendarDays, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { forwardRef, useEffect, useMemo, useState } from 'react';

import cn from '@/utils/cn';

import { Select as SelectMenu } from './select-menu';

interface DatePickerProps {
  // ISO date string, 'YYYY-MM-DD' (empty string when unset).
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  // Optional inclusive bounds, 'YYYY-MM-DD'. Days outside are not selectable.
  min?: string;
  max?: string;
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/**
 * Full month names, indexed by `Date#getMonth()` (0 = January). Exported
 * because `MisPeriodControl` and `AdminCompanySettingsPage` need the same
 * full-name list (calendar header vs. a FY-start note vs. a month <Select>) and
 * this calendar is the natural home for it - imported by direct path, same as
 * the other `components/ui` primitives (see `components/ui/index.ts`).
 */
export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

// How far the year dropdown reaches when the caller sets no bound. Wide enough
// backwards for a date of birth and forwards for a renewal/due date, which are
// the two ends this control is asked for across the app. A caller that passes
// min/max narrows it to exactly the range it allows.
const YEARS_BACK = 100;
const YEARS_FORWARD = 20;

const pad = (n: number): string => String(n).padStart(2, '0');
const toISO = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Parse 'YYYY-MM-DD' as a *local* date (not UTC) to avoid timezone day-shift.
const parseISO = (value: string | undefined): Date | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDisplay = (date: Date): string =>
  date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

// What the typeable field shows and accepts: DD-MM-YYYY, the order every other
// date in this app is READ in (formatDisplay above, the tables, the PDFs). The
// ISO string stays the wire format; this is only the human end of it.
const formatTyped = (date: Date): string =>
  `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;

/**
 * Parses what the user typed into a real date, or null.
 *
 * Accepts DD-MM-YYYY with `-`, `/` or `.` as the separator, and also a pasted
 * YYYY-MM-DD. The result is then re-checked field by field, because
 * `new Date(2026, 1, 31)` silently rolls over to 3 March - a control that turns
 * "31-02-2026" into a real selection is worse than one that refuses it.
 */
const parseTyped = (raw: string): Date | null => {
  const text = raw.trim();
  if (!text) return null;
  const iso = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(text);
  const dmy = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(text);
  const parts = iso
    ? { year: Number(iso[1]), month: Number(iso[2]), day: Number(iso[3]) }
    : dmy && { year: Number(dmy[3]), month: Number(dmy[2]), day: Number(dmy[1]) };
  if (!parts) return null;
  const { year, month, day } = parts;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
};

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const navButtonClass =
  'grid h-7 w-7 place-items-center rounded-md text-ink/60 transition-colors ' +
  'hover:bg-cream hover:text-ink';

// The month and year jumps, as Radix menus rather than native <select>s.
//
// They were native for two good reasons - free keyboard/screen-reader behaviour,
// and the platform picker on mobile, which is the fastest way to jump 40 years
// back. Radix keeps the first (it implements the listbox role and type-ahead)
// and gives up the second, which is the trade the rounded option list costs:
// a native list is painted by the OS and no stylesheet can round it, so these
// two were the last square popups in a calendar with rounded everything else.
//
// The old warning about `hover:bg-*` tinting the whole open list no longer
// applies for the same reason - the list is now drawn by the page, not the
// browser, so background states on the trigger stay on the trigger. The rule is
// kept anyway (state on the border, not the background) so the two header
// controls still match the footer's stepper buttons.
const headerSelectClass =
  'h-9 w-full rounded-xl border-line bg-white px-2 text-sm font-bold text-ink ' +
  'transition-colors hover:border-brand';

const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      label,
      error,
      helperText,
      placeholder = 'Select a date',
      disabled,
      id,
      className,
      min,
      max,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    // Recomputed each time the popover opens so the 'today' highlight and the
    // default view month stay correct in a long-lived session (past midnight).
    const [today, setToday] = useState(() => new Date());
    const selected = useMemo(() => parseISO(value), [value]);
    const minDate = useMemo(() => parseISO(min), [min]);
    const maxDate = useMemo(() => parseISO(max), [max]);
    const [view, setView] = useState<Date>(
      () => parseISO(value) ?? new Date(today.getFullYear(), today.getMonth(), 1),
    );
    // The typeable field holds its own draft rather than deriving from `value`
    // on every render: a half-typed "12-0" is not a date and must not be
    // reformatted mid-keystroke. Re-seeded whenever the committed value changes
    // underneath it (a day clicked in the grid, a form reset).
    const [typed, setTyped] = useState<string>(() => (selected ? formatTyped(selected) : ''));
    const [typedError, setTypedError] = useState(false);

    useEffect(() => {
      setTyped(selected ? formatTyped(selected) : '');
      setTypedError(false);
    }, [selected]);

    const grid = useMemo(() => {
      const year = view.getFullYear();
      const month = view.getMonth();
      const firstWeekday = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const cells: (Date | null)[] = [];
      for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
      for (let day = 1; day <= daysInMonth; day += 1) cells.push(new Date(year, month, day));
      return cells;
    }, [view]);

    // Bounded by min/max when the caller sets them, and always widened to include
    // the year currently in view: a stored date outside the allowed window (an
    // old row, a bound tightened later) must still be reachable in the dropdown
    // rather than silently absent from the control that claims to show it.
    const years = useMemo(() => {
      const base = today.getFullYear();
      const first = Math.min(minDate?.getFullYear() ?? base - YEARS_BACK, view.getFullYear());
      const last = Math.max(maxDate?.getFullYear() ?? base + YEARS_FORWARD, view.getFullYear());
      return Array.from({ length: last - first + 1 }, (_unused, i) => first + i);
    }, [today, minDate, maxDate, view]);

    const outOfRange = (d: Date): boolean =>
      (!!minDate && d.getTime() < minDate.getTime()) ||
      (!!maxDate && d.getTime() > maxDate.getTime());

    const shift = (yearDelta: number, monthDelta: number): void => {
      setView((prev) => new Date(prev.getFullYear() + yearDelta, prev.getMonth() + monthDelta, 1));
    };

    // Always the 1st of the target month, so switching from 31 March to February
    // lands on February rather than rolling over into March.
    const setViewPart = (part: 'month' | 'year', next: number): void => {
      setView(
        (prev) =>
          new Date(
            part === 'year' ? next : prev.getFullYear(),
            part === 'month' ? next : prev.getMonth(),
            1,
          ),
      );
    };

    const pick = (date: Date): void => {
      onChange(toISO(date));
      setOpen(false);
    };

    // Wipes the draft as well as the committed value. `onChange('')` alone was
    // enough while Clear only appeared for a selected date; now that it also
    // clears a typed draft, the local state has to go with it - when `value` is
    // already '' the `selected` effect never fires, so nothing else would reset
    // the field.
    const clear = (): void => {
      setTyped('');
      setTypedError(false);
      onChange('');
      setOpen(false);
    };

    /**
     * Commits the typed draft. Called on blur and on Enter, never per keystroke:
     * "1-2-20" is a valid prefix of a valid date and must not be committed as
     * the year 20.
     *
     * `moveView` is why this takes an argument at all. Blur fires on MOUSEDOWN,
     * so typing a date in another month and then clicking a day in the grid
     * would: blur the input, commit, jump the grid to the typed month, and only
     * THEN deliver the click - which now lands on a cell bound to a different
     * date than the one under the cursor when the user pressed the button. The
     * value still commits on blur; only the jump is withheld, so the grid the
     * user aimed at is the grid that receives the click.
     *
     * Enter has no such race (nothing else is about to be clicked) and does
     * move the view, which is what makes typing a far-off date and seeing it
     * work.
     */
    const commitTyped = (moveView: boolean): void => {
      if (!typed.trim()) {
        setTypedError(false);
        if (value) onChange('');
        return;
      }
      const parsed = parseTyped(typed);
      if (!parsed || outOfRange(parsed)) {
        setTypedError(true);
        return;
      }
      setTypedError(false);
      if (moveView) setView(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
      onChange(toISO(parsed));
    };

    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={id} className="text-sm font-medium text-ink">
            {label}
          </label>
        )}
        <Popover.Root
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (next) {
              const now = new Date();
              setToday(now);
              setView(selected ?? new Date(now.getFullYear(), now.getMonth(), 1));
              setTyped(selected ? formatTyped(selected) : '');
              setTypedError(false);
            }
          }}
        >
          <Popover.Trigger asChild>
            <button
              ref={ref}
              id={id}
              type="button"
              disabled={disabled}
              className={cn(
                'flex w-full items-center justify-between gap-2 rounded-md border bg-white py-2 pl-3 pr-3 text-left text-sm shadow-sm',
                'transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand',
                'disabled:cursor-not-allowed disabled:opacity-60',
                error ? 'border-danger focus:ring-danger focus:border-danger' : 'border-line',
                className,
              )}
            >
              <span className={cn(selected ? 'text-ink' : 'text-ink/50')}>
                {selected ? formatDisplay(selected) : placeholder}
              </span>
              <CalendarDays aria-hidden className="h-4 w-4 shrink-0 text-ink/40" />
            </button>
          </Popover.Trigger>

          <Popover.Portal>
            {/* z-[60], above the z-50 dialogs/sheets this calendar is often
                opened from. The month and year menus inside it must therefore be
                higher still - see the z-index scale documented in
                `select-menu.tsx`, which is the one place that records it. */}
            <Popover.Content
              align="start"
              sideOffset={6}
              className="z-[60] w-[17rem] rounded-xl border border-line bg-white p-3 shadow-lg"
            >
              {/* Type the whole date. The fastest path for anyone who already
                  knows it (a date of birth), and the only one that does not
                  need forty clicks on an arrow. */}
              <div className="mb-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={typed}
                  aria-label="Type a date as DD-MM-YYYY"
                  aria-invalid={typedError}
                  placeholder="DD-MM-YYYY"
                  onChange={(e) => {
                    setTyped(e.target.value);
                    setTypedError(false);
                  }}
                  onBlur={() => commitTyped(false)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    commitTyped(true);
                  }}
                  className={cn(
                    'w-full rounded-md border bg-white px-2 py-1.5 text-sm text-ink shadow-sm',
                    'transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand',
                    typedError
                      ? 'border-danger focus:ring-danger focus:border-danger'
                      : 'border-line',
                  )}
                />
                {typedError && (
                  <p className="mt-1 text-[11px] text-danger" role="alert">
                    Enter a real date as DD-MM-YYYY, inside the allowed range.
                  </p>
                )}
              </div>

              {/* Header: the two dropdowns and nothing else. Jumping to a month
                  or a year is the common move; stepping one at a time is the
                  fallback, and it lives in the footer below. They share the
                  width rather than sizing to their content, so the header does
                  not resize as the label goes from "May" to "September". */}
              <div className="mb-2 flex items-center gap-1.5">
                <div className="flex-[3]">
                  <SelectMenu
                    aria-label="Month"
                    value={String(view.getMonth())}
                    onChange={(e) => setViewPart('month', Number(e.target.value))}
                    className={headerSelectClass}
                  >
                    {MONTHS.map((name, index) => (
                      <option key={name} value={index}>
                        {name}
                      </option>
                    ))}
                  </SelectMenu>
                </div>
                <div className="flex-[2]">
                  <SelectMenu
                    aria-label="Year"
                    value={String(view.getFullYear())}
                    onChange={(e) => setViewPart('year', Number(e.target.value))}
                    className={headerSelectClass}
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </SelectMenu>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-0.5">
                {WEEKDAYS.map((wd) => (
                  <div
                    key={wd}
                    className="grid h-8 place-items-center text-[11px] font-semibold uppercase text-ink/40"
                  >
                    {wd}
                  </div>
                ))}
                {grid.map((date, i) => {
                  if (!date) {
                    // eslint-disable-next-line react/no-array-index-key
                    return <div key={`blank-${i}`} className="h-8" />;
                  }
                  const isSelected = selected ? sameDay(date, selected) : false;
                  const isToday = sameDay(date, today);
                  const disabledDay = outOfRange(date);
                  return (
                    <button
                      key={toISO(date)}
                      type="button"
                      disabled={disabledDay}
                      onClick={() => pick(date)}
                      className={cn(
                        'grid h-8 w-full place-items-center rounded-md text-sm transition-colors',
                        isSelected
                          ? 'bg-brand font-semibold text-white hover:bg-brand-hover'
                          : 'text-ink hover:bg-cream',
                        !isSelected && isToday && 'font-semibold text-brand',
                        disabledDay && 'cursor-not-allowed text-ink/25 hover:bg-transparent',
                      )}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>

              {/* Footer: the step arrows on the left, Clear pinned bottom-right.
                  Clear is ALWAYS rendered, not only when a date is set - a
                  control that appears and disappears makes the popover change
                  height as you use it, and moves whatever you were about to
                  click. It is disabled instead when there is nothing to clear,
                  which says the same thing without the reflow.

                  "Nothing to clear" counts the typed draft as well as the
                  committed value: a half-typed date the user wants to abandon is
                  exactly when Clear is reached for, and it has no `selected` yet. */}
              <div className="mt-2 flex items-center justify-between gap-1 border-t border-line pt-2">
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => shift(-1, 0)}
                    aria-label="Previous year"
                    className={navButtonClass}
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => shift(0, -1)}
                    aria-label="Previous month"
                    className={navButtonClass}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => shift(0, 1)}
                    aria-label="Next month"
                    className={navButtonClass}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => shift(1, 0)}
                    aria-label="Next year"
                    className={navButtonClass}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={clear}
                  disabled={!selected && !typed.trim()}
                  className={cn(
                    'rounded-md px-2 py-1 text-xs font-semibold text-ink/60 transition-colors',
                    'hover:bg-cream hover:text-ink',
                    'disabled:cursor-not-allowed disabled:text-ink/25 disabled:hover:bg-transparent',
                  )}
                >
                  Clear
                </button>
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
        {error && (
          <p className="text-xs text-danger" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && <p className="text-xs text-ink/60">{helperText}</p>}
      </div>
    );
  },
);

DatePicker.displayName = 'DatePicker';

export default DatePicker;
